import { ButtonContainer, IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router";
import { SuiviDocumentEditDialog, SuiviDocumentUploadDialog } from "../../..";
import { SuiviApi, SuiviFichierApi, SuiviFichierGridDto, SuiviFichierSearchDto } from "../../../../../api";
import { DeleteButton, DownloadButton, EditButton, SearchTablePage } from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { exportFile } from "../../../../../utils";

export interface ISuiviDocumentsProps {}

export const SuiviDocuments: React.FunctionComponent<ISuiviDocumentsProps> = props => {
  const { t } = useTl();
  const api = useApiService(SuiviApi);
  const searchApi = useApiService(SuiviFichierApi);
  const { id } = useParams<{ id: string }>();
  const [currentSuiviFichierId, setCurrentSuiviFichierId] = React.useState(null);
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { dispatchEvent } = useEventsContext();
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idsuiviFichier);
          await api.suiviDeleteSuiviFichier({ id: idsuiviFichier });
          dispatchEvent("SEARCH_TABLE_REFRESH");
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      try {
        setRowLoading(idsuiviFichier);
        const file = await api.suiviDownloadSuiviFichier({ id: idsuiviFichier });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: SuiviFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentSuiviFichierId(row.idsuiviFichier)}
              loading={rowLoading === row.idsuiviFichier}
              disabled={!!rowLoading && rowLoading !== row.idsuiviFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.idsuiviFichier)}
              loading={rowLoading === row.idsuiviFichier}
              disabled={!!rowLoading && rowLoading !== row.idsuiviFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.idsuiviFichier)}
              loading={rowLoading === row.idsuiviFichier}
              disabled={!!rowLoading && rowLoading !== row.idsuiviFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeSuiviFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const getSearchCriterias = React.useCallback(
    () => searchApi.suiviFichierGetSearchCriterias({ includeListsValues: true }),
    [searchApi]
  );

  const apiFactory = useAbortableApiServiceFactory(SuiviFichierApi);
  const lastAbortController = React.useRef<AbortController>();
  const search = React.useCallback(
    (nextSearch?: SuiviFichierSearchDto) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.suiviFichierBaseSearch({ SuiviFichierSearchDto: { ...nextSearch, idsuivi: +id } });
    },
    [apiFactory, id]
  );

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentSuiviFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        dispatchEvent("SEARCH_TABLE_REFRESH");
      }
    },
    [dispatchEvent]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <>
      <SearchTablePage
        columns={columns}
        withCard={false}
        getCriteriasFunction={getSearchCriterias}
        searchFunction={search}
        onAbort={onAbort}
        sortKeys={{ uploadDate: "DESC", fileName: "ASC" }}
        addFunc={onAdd}
      />
      {uploadDialoOpen && <SuiviDocumentUploadDialog onClose={onClose} dialogOpen={uploadDialoOpen} idsuivi={+id} />}
      {!!currentSuiviFichierId && <SuiviDocumentEditDialog onClose={onClose} idsuiviFichier={currentSuiviFichierId} />}
    </>
  );
};
